import { Users, User, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';

interface Participant {
  id: string;
  user_id: string;
  player1_name: string;
  player2_name: string | null;
  registered_at: string;
  group: string | null;
}

interface ParticipantsListProps {
  participants: Participant[];
  currentUserId: string | null;
  loading: boolean;
  subcategory: string;
  category: string;
}

export default function ParticipantsList({
  participants,
  currentUserId,
  loading,
  subcategory,
  category
}: ParticipantsListProps) {
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            Cargando participantes...
          </div>
        </CardContent>
      </Card>
    );
  }



  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <span className={`${isMobile ? 'text-base' : 'text-lg'} font-bold`}>
              Participantes Inscritos
            </span>
          </div>
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
            {participants.length} {participants.length === 1 ? 'pareja' : 'parejas'}
          </Badge>
        </CardTitle>
        <div className="text-sm text-muted-foreground mt-2">
          Categoría: <span className="font-semibold">{category}</span> • 
          Subcategoría: <span className="font-semibold">{subcategory}</span>
        </div>
      </CardHeader>
      
      <CardContent className="px-0">
        <ScrollArea className={isMobile ? "h-[400px]" : "h-[500px]"}>
          <div className="space-y-2 px-4">
            {participants.map((participant, index) => {
              const isCurrentUser = participant.user_id === currentUserId;
              const displayName = participant.player2_name
                ? `${participant.player1_name} / ${participant.player2_name}`
                : participant.player1_name;

              return (
                <div
                  key={participant.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    isCurrentUser
                      ? 'bg-primary/10 border-primary/30 shadow-sm'
                      : 'bg-card border-border hover:border-muted-foreground/30'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Position Number */}
                    <div className="flex-shrink-0">
                      {index === 0 ? (
                        <Crown className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <div className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-muted-foreground min-w-[24px] text-center`}>
                          #{index + 1}
                        </div>
                      )}
                    </div>

                    {/* Player Names */}
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`${isMobile ? 'text-xs' : 'text-base'} font-medium ${
                        isCurrentUser ? 'text-primary' : 'text-foreground'
                      }`}>
                        {displayName}
                      </span>
                      {isCurrentUser && (
                        <Badge className="bg-primary text-primary-foreground text-xs flex-shrink-0">
                          TÚ
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Group Badge */}
                  {participant.group && (
                    <Badge variant="outline" className="text-xs flex-shrink-0 ml-2">
                      {participant.group}
                    </Badge>
                  )}
                </div>
              );
            })}

            {participants.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No hay participantes inscritos en esta subcategoría</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
